import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { GOOGLE_REVIEWS_URL } from '../data/constants';
import { trackEvent } from '../hooks/useAnalytics';

export const TestimonialsCarousel = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-scroll every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="relative max-w-3xl mx-auto">
      {/* Navigation Buttons */}
      <button
        onClick={goToPrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-6 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-primary hover:border-primary transition-all hover:scale-110"
        aria-label="Avis précédent"
      >
        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-6 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-primary hover:border-primary transition-all hover:scale-110"
        aria-label="Avis suivant"
      >
        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>

      {/* Single Testimonial Card */}
      <div className="px-8 sm:px-16">
        <div 
          className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 transition-all duration-500"
        >
          {/* Quote icon */}
          <div className="text-primary/30 text-4xl sm:text-5xl font-serif leading-none mb-4">"</div>
          
          {/* Rating */}
          <div className="flex justify-center mb-4">
            {[...Array(currentTestimonial.rating)].map((_, i) => (
              <Star key={i} className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          
          {/* Text */}
          <p className="text-gray-300 text-base sm:text-lg text-center mb-6 leading-relaxed">
            {currentTestimonial.text}
          </p>
          
          {/* Author */}
          <div className="flex items-center justify-center gap-3 pt-4 border-t border-white/10">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {currentTestimonial.name.charAt(0).toUpperCase()}
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">{currentTestimonial.name}</p>
              <div className="flex items-center gap-2 flex-wrap">
                {currentTestimonial.source && (
                  <span className="flex items-center gap-1 text-xs text-blue-400 font-medium">
                    <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor"><path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/></svg>
                    {currentTestimonial.source}
                  </span>
                )}
                {currentTestimonial.date && (
                  <span className="text-xs text-gray-500">{currentTestimonial.date}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-6">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setIsAutoPlaying(false);
              setCurrentIndex(index);
              setTimeout(() => setIsAutoPlaying(true), 10000);
            }}
            className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? "bg-primary w-6 sm:w-8" 
                : "bg-white/30 hover:bg-white/50"
            }`}
            aria-label={`Aller à l'avis ${index + 1}`}
          />
        ))}
      </div>

      {/* Counter + Google Reviews CTA */}
      <div className="text-center mt-6 flex flex-col items-center gap-3">
        <div className="text-gray-500 text-sm">
          {currentIndex + 1} / {testimonials.length} avis
        </div>
        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('google_reviews_click', { event_category: 'Trust', event_label: 'Testimonials CTA' })}
          className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 border border-blue-400/30 hover:border-blue-400/60 px-4 py-2 rounded-full transition-all duration-300 hover:bg-blue-400/10"
          data-testid="google-reviews-link"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="currentColor">
            <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
          </svg>
          Voir tous les {testimonials.length} avis sur Google
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

// Header Component with FAQ badge, Parking, and Language Switcher
